import { TimeString } from './models'
import { PortNumber } from './ports'

/**
 * A generic API response indicating success.
 */
export interface SuccessResponse<Type = Record<string, unknown>> {
  success: true
  data: Type
}

/**
 * Check if the response is successful.
 */
export function isSuccessResponse(obj: unknown): obj is SuccessResponse {
  return obj !== null &&
    typeof obj === 'object' &&
    (obj as { success: unknown}).success === true &&
    typeof (obj as { data: unknown}).data === 'object'
}

/**
 * A mapping from error topics to the list messages related to the topic.
 */
export type ErrorMessages = Record<string, string[]>

/**
 * A generic API response indicating failure.
 */
export interface ErrorResponse {
  success: false
  messages: ErrorMessages
}

/**
 * Check if the response is not successful.
 */
export function isErrorResponse(obj: unknown): obj is ErrorResponse {
  return obj !== null &&
    typeof obj === 'object' &&
    (obj as { success: unknown}).success === false &&
    typeof (obj as { messages: unknown}) === 'object'
}

/**
 * API call response either success or failure.
 */
export type Response<Type = unknown> = SuccessResponse<Type> | ErrorResponse

/**
 * Telemetry data format.
 */
export interface PortStatistics {
  avg: number
  count: number
  max: number
  min: number
}

export function isPortStatistics(obj: unknown): obj is PortStatistics {
  return obj !== null &&
    typeof obj === 'object' &&
    'avg' in obj &&
    typeof (obj as any).avg === 'number' &&
    'count' in obj &&
    typeof (obj as any).count === 'number' &&
    'max' in obj &&
    typeof (obj as any).max === 'number' &&
    'min' in obj &&
    typeof (obj as any).min === 'number'
}

export type CounterName = 'info' | 'curconf' | 'do' | 'setconf' | 'getconf' | 'flash' | 'confmit' | 'other' | 'total'

export type CounterData = Partial<Record<CounterName, number>>

export type TelemetryData = {
  started: TimeString
  counters: CounterData
  latest: Record<PortNumber, number>
} & Record<PortNumber, PortStatistics>
